import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { Award, ArrowLeft, ArrowRight, Shield, CheckCircle, ExternalLink } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Qualifications RGE : QualiPAC, QualiSol, QualiBois, Qualifelec, QualiPV | AfficheServices' },
  { name: 'description', content: 'Les qualifications RGE en France : à quoi elles correspondent, quelles aides elles débloquent, comment vérifier un artisan.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/rge/qualifications' },
]

interface Qualification {
  code: string
  name: string
  organism: string
  covers: string
  aides: string
  detail: string
}

const QUALIFICATIONS: Qualification[] = [
  {
    code: 'QualiPAC',
    name: 'QualiPAC Chauffage & ECS',
    organism: "Qualit'EnR",
    covers: 'Pompes à chaleur air/eau, eau/eau, hybrides, CET',
    aides: "CEE, MaPrimeRénov', écoPTZ, TVA 5,5 %",
    detail:
      "La mention QualiPAC est délivrée par Qualit'EnR et couvre l'installation de pompes à chaleur de chauffage et/ou d'eau chaude sanitaire. Obligatoire pour bénéficier des aides sur les PAC air/eau et géothermiques. Module \"Froid\" séparé pour la climatisation.",
  },
  {
    code: 'QualiBois',
    name: 'QualiBois Air & Module',
    organism: "Qualit'EnR",
    covers: 'Poêles et chaudières à bois bûches, granulés, plaquettes',
    aides: "CEE, MaPrimeRénov', Coup de pouce, TVA 5,5 %",
    detail:
      "QualiBois a deux modules : \"Air\" pour les poêles indépendants, \"Module\" pour les chaudières raccordées au chauffage central. L'artisan doit avoir le module correspondant au geste réalisé — un QualiBois Air ne peut pas installer une chaudière biomasse hydraulique.",
  },
  {
    code: 'QualiSol',
    name: 'QualiSol Chauffage & CESI',
    organism: "Qualit'EnR",
    covers: 'Chauffe-eau solaires individuels, systèmes solaires combinés',
    aides: "CEE, MaPrimeRénov', TVA 5,5 %",
    detail:
      'La mention QualiSol concerne le solaire thermique (production d\'eau chaude et de chauffage). Elle est distincte de QualiPV (photovoltaïque). Pour un chantier mixte (CESI + PV), l\'artisan doit détenir les deux qualifications.',
  },
  {
    code: 'QualiPV',
    name: 'QualiPV Élec',
    organism: "Qualit'EnR",
    covers: 'Panneaux photovoltaïques, onduleurs, micro-onduleurs',
    aides: "Prime à l'autoconsommation, CEE partiels, TVA 10 %",
    detail:
      "QualiPV Élec couvre les installations photovoltaïques raccordées au réseau (autoconsommation ou revente totale). Le module \"Bat\" est requis pour les installations en toiture. Obligatoire pour la prime à l'autoconsommation versée par l'État.",
  },
  {
    code: 'Qualifelec RGE',
    name: 'Qualifelec RGE',
    organism: 'Qualifelec',
    covers: 'Installations électriques basse tension, rénovation élec',
    aides: "CEE (ventilation mécanique), MaPrimeRénov' Parcours Accompagné",
    detail:
      "Qualifelec RGE est la mention RGE de l'organisme Qualifelec, spécialisé dans l'électricité. Elle couvre les travaux de rénovation électrique dans le cadre de projets globaux de rénovation énergétique (VMC, éclairage LED, pilotage, automatisation).",
  },
  {
    code: 'Qualibat RGE',
    name: 'Qualibat RGE (ITE, ITI, menuiserie, toiture)',
    organism: 'Qualibat',
    covers: 'Isolation par l\'extérieur, intérieur, menuiseries, toiture',
    aides: "CEE, MaPrimeRénov', écoPTZ, TVA 5,5 %",
    detail:
      "Qualibat est l'organisme historique de qualification du bâtiment. Sa mention RGE couvre l'essentiel des gestes d'isolation et de menuiserie. Chaque geste a un code Qualibat précis : 7141 (ITE), 7233 (ITI), 4311 (toiture), etc. L'artisan doit détenir le code correspondant au geste réalisé.",
  },
]

export default function RGEQualificationsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'RGE', url: 'https://www.afficheservices.fr/rge' },
        { name: 'Qualifications', url: 'https://www.afficheservices.fr/rge/qualifications' },
      ]} />

      <section className="bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Link to="/rge" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Retour RGE
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-emerald-200">
            <Award className="h-3.5 w-3.5" /> 6 qualifications principales
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">
            Qualifications RGE &mdash; panorama complet
          </h1>
          <p className="mt-3 max-w-2xl text-base text-emerald-200">
            Le label RGE (Reconnu Garant de l'Environnement) regroupe 6 qualifications délivrées
            par 3 organismes. Chaque geste technique nécessite la qualification adéquate.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900">
              <p className="font-bold">Un artisan peut être RGE sur certains gestes et pas sur d'autres.</p>
              <p className="mt-1">
                Exemple : un artisan RGE QualiPAC peut installer une pompe à chaleur et donner droit aux aides,
                mais ne peut pas vous faire bénéficier des CEE sur une chaudière biomasse (il faudrait QualiBois).
                Vérifiez la qualification précise pour chaque opération.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {QUALIFICATIONS.map((q) => (
            <div key={q.code} className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                      <Award className="h-3 w-3" /> {q.code}
                    </span>
                    <span className="text-xs text-slate-400">par {q.organism}</span>
                  </div>
                  <h2 className="mt-2 text-lg font-bold text-slate-900">{q.name}</h2>
                </div>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{q.detail}</p>
              <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-3">
                  <dt className="font-semibold text-slate-500">Gestes couverts</dt>
                  <dd className="mt-1 text-slate-900">{q.covers}</dd>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <dt className="font-semibold text-slate-500">Aides débloquées</dt>
                  <dd className="mt-1 text-slate-900">{q.aides}</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>

        <section className="mt-12 rounded-2xl border border-border bg-blue-50 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <CheckCircle className="h-5 w-5 text-primary" /> Vérifier un artisan RGE
          </h2>
          <p className="mt-2 text-sm text-slate-700">
            L'annuaire officiel France Rénov' permet de vérifier gratuitement la validité d'une qualification RGE
            par nom d'entreprise, SIRET ou département.
          </p>
          <a
            href="https://france-renov.gouv.fr/annuaire-rge"
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover"
          >
            Annuaire officiel RGE <ExternalLink className="h-4 w-4" />
          </a>
        </section>

        <section className="mt-8 text-center">
          <Link
            to="/rge/sources"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          >
            Méthodologie &amp; sources ADEME <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
